import { HStack } from '@sgme/ui';
import clsx from 'clsx';
import { FormattedMessage } from 'react-intl';

import { MarginValue } from '@/App/tiles/CashTile/shared/MarginValue.tsx';
import type { BidAskPair } from '@/App/tiles/shared/CommercialMargin/types.ts';
import type { IntlMessageId } from '@/types/intl';

import { MarginControlButtons } from './MarginControlButtons.tsx';
import { useMarginFieldEditor } from './useMarginFieldEditor.ts';

/**
 * Configuration of a margin section
 */
export type MarginSectionProps = {
  /**
   * Type of the section (for identification)
   */
  type: 'spot' | 'swap';

  /**
   * Translation key for the label
   */
  labelKey?: IntlMessageId;

  /**
   * Label in plain text (if no i18n key)
   */
  label?: string;

  /**
   * Current value
   */
  value: BidAskPair;

  /**
   * Default value (for reset)
   */
  defaultValue?: BidAskPair;

  /**
   * Increment for +/- buttons
   */
  increment: number;

  /**
   * Indicates if the value is editable
   */
  isEditable: boolean;

  /**
   * Optional converter
   */
  converter?: MarginConverter;

  /**
   * Minimum number of decimals to display
   */
  minimumFractionDigits?: number;

  /**
   * Maximum number of decimals to display
   */
  maximumFractionDigits?: number;

  /**
   * Prefix for data-e2e attributes
   */
  dataE2ePrefix: string;

  /**
   * Callback called when the value is updated
   */
  onUpdate: (value: BidAskPair) => void;

  /**
   * Callback called on reset
   */
  onReset?: () => void;
};

/**
 * Converter to transform displayed/edited values
 */
export type MarginConverter = {
  /**
   * Converts an internal value to a displayable value
   */
  toDisplay: (value: BidAskPair) => BidAskPair;

  /**
   * Converts a displayable value to an internal value
   */
  fromDisplay: (value: BidAskPair) => BidAskPair;

  /**
   * Converts a pair to a string for editing
   */
  toString: (value: BidAskPair) => string;

  /**
   * Parses an edited string to a pair
   * @returns undefined if parsing fails
   */
  fromString: (value: string) => BidAskPair | undefined;
};

/**
 * Section éditable pour une marge (spot ou swap)
 * Affiche le label, les boutons de contrôle, et le champ éditable
 */
export function MarginSection(props: MarginSectionProps) {
  const {
    labelKey,
    label,
    value,
    increment,
    isEditable,
    converter,
    minimumFractionDigits = 1,
    maximumFractionDigits = 2,
    dataE2ePrefix,
    onUpdate,
    onReset,
  } = props;

  const editor = useMarginFieldEditor({
    value,
    converter,
    onSave: onUpdate,
  });

  const handleUpdateValue = (bidDelta: number, askDelta: number) => () => {
    const displayValue = converter ? converter.toDisplay(value) : value;

    const newDisplayValue = {
      bid: displayValue.bid + bidDelta,
      ask: displayValue.ask + askDelta,
    };

    const newInternalValue = converter ? converter.fromDisplay(newDisplayValue) : newDisplayValue;

    onUpdate(newInternalValue);
  };

  const displayValue = converter ? converter.toDisplay(value) : value;

  const handleEdit = () => {
    if (!isEditable) {
      return;
    }

    editor.startEditing();
  };

  return (
    <>
      {/* Ligne de contrôle */}
      <div />

      <MarginControlButtons
        onDecrement={handleUpdateValue(-increment, -increment)}
        onIncrement={handleUpdateValue(increment, increment)}
        onShiftLeft={handleUpdateValue(increment, -increment)}
        onShiftRight={handleUpdateValue(-increment, increment)}
        onReset={onReset}
        isEditable={isEditable}
        dataE2ePrefix={dataE2ePrefix}
      />

      <div />

      {/* Label */}
      <div className="fs-12 text-secondary fw-bold text-end">
        {labelKey ? <FormattedMessage id={labelKey} /> : label}
      </div>

      {/* Champ éditable */}
      <div>
        {editor.isFocused ? (
          <input
            ref={editor.inputRef}
            disabled={!isEditable}
            onChange={editor.handleChange}
            onFocus={(e) => e.target.select()}
            onBlur={editor.saveValue}
            value={editor.editedValue}
            autoComplete="off"
            className={clsx('form-control form-control-sm text-center', {
              'is-invalid': editor.isInvalid,
            })}
            data-e2e={`${dataE2ePrefix}-input`}
          />
        ) : (
          <HStack
            horizontalAlignment="center"
            onClick={handleEdit}
            className="form-control form-control-sm"
            data-e2e={dataE2ePrefix}
          >
            <MarginValue
              value={displayValue.bid}
              minimumFractionDigits={minimumFractionDigits}
              maximumFractionDigits={maximumFractionDigits}
              isEditable={isEditable}
              data-e2e={`${dataE2ePrefix}-bid`}
            />
            <span>/</span>
            <MarginValue
              value={displayValue.ask}
              minimumFractionDigits={minimumFractionDigits}
              maximumFractionDigits={maximumFractionDigits}
              isEditable={isEditable}
              data-e2e={`${dataE2ePrefix}-ask`}
            />
          </HStack>
        )}
      </div>

      <div />
    </>
  );
}
